import React from 'react';
import cn from 'classnames';
// Style
import styles from './DetailList.module.scss';

type DetailCardProps = {
  title: string;
  /** Controls that belong to this group of fields, shown beside the title. */
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
};

/** A named group of read-only fields. */
export const DetailCard = ({
  title,
  actions,
  className,
  children,
}: DetailCardProps): JSX.Element => (
  <section className={cn(styles.card, className)}>
    <header className={styles.cardHeader}>
      <h2 className={styles.cardTitle}>{title}</h2>
      {actions}
    </header>
    {children}
  </section>
);

type DetailGridProps = {
  className?: string;
  children: React.ReactNode;
};

/** Flows fields across the available width instead of down one column. */
export const DetailGrid = ({
  className,
  children,
}: DetailGridProps): JSX.Element => (
  <div className={cn(styles.grid, className)}>{children}</div>
);

type DetailItemProps = {
  label: string;
  value?: React.ReactNode;
  /** Give the field the whole line. For long text and lists. */
  wide?: boolean;
  /** Set the value in a monospaced face. For ids, keys and hashes. */
  mono?: boolean;
  /** Shown when the record has no value for this field. */
  emptyText?: string;
  /** A note under the value, for fields whose number needs explaining. */
  hint?: React.ReactNode;
};

const isEmpty = (value: React.ReactNode) =>
  value === null || value === undefined || value === '';

export const DetailItem = ({
  label,
  value,
  wide,
  mono,
  emptyText = 'Not set',
  hint,
}: DetailItemProps): JSX.Element => {
  const empty = isEmpty(value);

  return (
    <div className={cn(styles.item, wide && styles.wide)}>
      <p className={styles.label}>{label}</p>
      <div
        className={cn(styles.value, mono && styles.mono, empty && styles.empty)}
      >
        {empty ? emptyText : value}
      </div>
      {hint && <p className={styles.hint}>{hint}</p>}
    </div>
  );
};
